import React from 'react';
import { Box, Stack, Paper, Button, Divider } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import GavelIcon from '@mui/icons-material/Gavel';
import PieChartIcon from '@mui/icons-material/PieChart';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterDropdown from './FilterDropdown';
import { COLOR_PRIMARY_1, COLOR_PRIMARY_2, COLOR_PRIMARY_2_600 } from 'src/colors';
import SearchIcon from '@mui/icons-material/Search';
// Placeholder imports for FilterDropdown and Chip
// import FilterDropdown from './FilterDropdown';
// import FilterChip from 'src/App/Components/Chips/FilterChip';

// Moved options arrays to parent component
// const regulationOptions = [...];
// const marketSizeOptions = [...];
// const sortByOptions = [...];
// const countryOptions = [...];

// Define common styles for all buttons
const commonButtonSx = {
  justifyContent: 'flex-start',
  boxShadow: 'none',
};

// Define styles specific to the contained (active) state
const containedButtonSx = {
  backgroundColor: COLOR_PRIMARY_2_600,
  color: 'white',
  borderColor: COLOR_PRIMARY_2,
  boxShadow: 1,
  '&:hover': {
    backgroundColor: COLOR_PRIMARY_2_600,
    borderColor: COLOR_PRIMARY_2_600,
    boxShadow: 3,
  },
};

// Define styles specific to the outlined (inactive) state
const outlinedButtonSx = {
  borderColor: COLOR_PRIMARY_2,
  color: COLOR_PRIMARY_2,
  backgroundColor: 'transparent',
  '&:hover': {
    backgroundColor: 'rgba(66, 85, 99, 0.04)',
    borderColor: COLOR_PRIMARY_2,
    color: COLOR_PRIMARY_2,
  },
};

// Define Props Interface
interface OptionType {
  value: string;
  label: string;
  isState?: boolean;
  countryCode?: string;
}

interface FilterBarProps {
  selectedLocations: string[];
  selectedRegulations: string[];
  selectedMarketSizes: string[];
  handleLocationsChange: (val: string[]) => void;
  handleRegulationsChange: (val: string[] | string) => void;
  handleMarketSizesChange: (val: string[] | string) => void;
  countryOptions: OptionType[];
  regulationOptions: OptionType[];
  marketSizeOptions: OptionType[];
  onFilterChangeAccessCheckMiddleware: (val: string | string[], actionFunction: any) => void;
  openSearchModal: () => void;
  isMobileScreen: boolean;
}

const FilterBar: React.FC<FilterBarProps> = ({
  selectedLocations,
  selectedRegulations,
  selectedMarketSizes,
  handleLocationsChange,
  handleRegulationsChange,
  handleMarketSizesChange,
  countryOptions,
  regulationOptions,
  marketSizeOptions,
  onFilterChangeAccessCheckMiddleware,
  openSearchModal,
  isMobileScreen,
}) => {
  const SearchButton = (
    <Button
      variant="contained"
      startIcon={<SearchIcon />}
      sx={{
        ...commonButtonSx,
        ...containedButtonSx,
        background: COLOR_PRIMARY_2,
      }}
      disableElevation
      onClick={openSearchModal}
    >
      Search
    </Button>
  );

  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        overflowX: 'auto',
        position: 'sticky',
        top: 0,
        zIndex: 10,
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ pr: 2, py: 1, minWidth: 'max-content' }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <FilterDropdown
            label="Location"
            button={
              <Button
                variant={selectedLocations.length > 0 ? 'contained' : 'outlined'}
                startIcon={<LocationOnIcon />}
                endIcon={<ExpandMoreIcon />}
                sx={{
                  ...commonButtonSx,
                  ...(selectedLocations.length > 0 ? containedButtonSx : outlinedButtonSx),
                }}
                disableElevation
              >
                Location
              </Button>
            }
            options={countryOptions}
            value={selectedLocations}
            onChange={val => handleLocationsChange(val as string[])}
            multiSelect
            selectedCountBadge
            placeholder="Search country or US state"
          />
          <FilterDropdown
            label="Regulation"
            button={
              <Button
                variant={selectedRegulations.length > 0 ? 'contained' : 'outlined'}
                startIcon={<GavelIcon />}
                endIcon={<ExpandMoreIcon />}
                sx={{
                  ...commonButtonSx,
                  ...(selectedRegulations.length > 0 ? containedButtonSx : outlinedButtonSx),
                }}
                disableElevation
              >
                Regulation
              </Button>
            }
            options={regulationOptions}
            value={selectedRegulations}
            onChange={val => onFilterChangeAccessCheckMiddleware(val, handleRegulationsChange)}
            multiSelect
            selectedCountBadge
            placeholder="Search regulation"
          />
          <FilterDropdown
            label="Market Size"
            button={
              <Button
                variant={selectedMarketSizes.length > 0 ? 'contained' : 'outlined'}
                startIcon={<PieChartIcon />}
                endIcon={<ExpandMoreIcon />}
                sx={{
                  ...commonButtonSx,
                  ...(selectedMarketSizes.length > 0 ? containedButtonSx : outlinedButtonSx),
                }}
                disableElevation
              >
                Market Size
              </Button>
            }
            options={marketSizeOptions}
            value={selectedMarketSizes}
            onChange={val => onFilterChangeAccessCheckMiddleware(val, handleMarketSizesChange)}
            multiSelect
            selectedCountBadge
            placeholder="Search market size"
          />
          {!isMobileScreen && SearchButton}
        </Stack>
        {isMobileScreen && (
          <Box sx={{my: '10px'}}>{SearchButton}</Box>
        )}
      </Box>
    </Paper>
  );
};

export default FilterBar;
